import classnames from 'classnames'
import React, { type FC, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Avatar, Button, type IColumn, IconButton, Table, Typography } from 'shared/ui'
import { TableSearch } from 'shared/ui/TableNew'
import { type IntegrationCrmUser } from 'entities/Integrations/model/IntegrationCrmUser'
import { type ICrmUserIntegrationMeta } from 'entities/Integrations'
import { useEventIntegrationUsersRefreshed } from 'pages/settings/pages/integrations/pages/integrationSettings/events/EventIntegrationUsersRefreshed'
import type { IntegrationSettingsUsersStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsUsersStore'
import { IntegrationSettingsUsersCell } from './IntegrationSettingsUsersCell'
import styles from './styles.module.scss'

type IIntegrationSettingsUsersProps = {
  store: IntegrationSettingsUsersStore
}

export const IntegrationSettingsUsers: FC<IIntegrationSettingsUsersProps> = observer(
  ({ store }) => {
    const {
      users,
      pagination,
      isLoading,
      sendRefreshRequest,
      total,
      onChangePagination,
      search,
      onSearchChange,
      organizationMembers,
      onUserChange,
      refreshData,
      tableStore,
      onGroupInvite,
      onUserInvite,
      onManageInvite,
      usersCount,
      status,
      integrationName,
      isCalendlyIntegration,
      canAutoMapUsers,
      autoMapUsers,
      scrollToTOpTrigger,
    } = store

    // refresh event subscriber
    useEventIntegrationUsersRefreshed(refreshData)

    const COLUMNS: IColumn<IntegrationCrmUser<ICrmUserIntegrationMeta>>[] = useMemo(
      () => [
        {
          field: 'user',
          name: `${integrationName} user`,
          width: '40%',
          tooltipQuestionProps: isCalendlyIntegration
            ? {
                placement: 'top',
                label: [
                  <Typography
                    key={`${integrationName}_tooltip`}
                    variant={'body-md-regular'}
                    ariaLabel={`${integrationName}Tooltip`}
                    className={styles.tooltipText}
                  >
                    Admins and Owners already have access
                    <br /> to all meetings in your organization,
                    <br /> but you can still map them if needed.
                  </Typography>,
                ],
              }
            : undefined,
          renderRowCell: ({ avatarInfo, name, email }) => (
            <div className={styles.row}>
              <Avatar info={avatarInfo} size={32} />
              <div className={styles.content}>
                <Typography
                  variant={'body-md-regular'}
                  ellipsis
                  tooltipProps={{ label: name }}
                  ariaLabel={'displayInfo'}
                >
                  {name}
                </Typography>
                <Typography
                  variant={'body-sm-regular'}
                  ellipsis
                  ariaLabel={'email'}
                  color={'tertiary'}
                >
                  {email}
                </Typography>
              </div>
            </div>
          ),
        },
        {
          field: 'select',
          name: 'Salesmsg user',
          renderHeaderCell: (column) => (
            <div className={styles.refreshCell}>
              <div>{column.name}</div>
              <IconButton
                className={styles.headerIcon}
                iconProps={{ fontSize: 16 }}
                tooltipProps={{
                  label: 'Resync auto-maps any unmapped users. Existing mappings won’t be changed.',
                  width: 270,
                  placement: 'top',
                }}
                variant={'icon'}
                color={'tertiary'}
                size={'small'}
                icon={isLoading ? 'loading3' : 'refresh'}
                ariaLabel='Refresh_data'
                onClick={sendRefreshRequest}
              />
            </div>
          ),
          renderRowCell: (row) => (
            <IntegrationSettingsUsersCell
              row={row}
              members={organizationMembers}
              onUserChange={onUserChange}
              onUserInvite={onUserInvite}
              onManageInvite={onManageInvite}
            />
          ),
        },
      ],
      [
        organizationMembers,
        onUserChange,
        refreshData,
        onUserInvite,
        onManageInvite,
        isLoading,
        integrationName,
      ]
    )

    if (!usersCount && !isCalendlyIntegration) {
      return (
        <div className={styles.placeholder}>
          <Typography
            variant={'body-md-regular'}
            ellipsis
            ariaLabel={'empty_placeholder'}
            color={'tertiary'}
          >
            There are no {integrationName} users
          </Typography>
        </div>
      )
    }

    const dataNotExist = !total && !status.isLoading && !status.isIdle
    const isShowBottomTableContent = isCalendlyIntegration ? dataNotExist : dataNotExist && search

    return (
      <div className={styles.root}>
        {canAutoMapUsers && (
          <Alert
            item={{
              type: 'infoBlue',
              title: 'Sync users automatically',
              desc: `Quickly match your ${integrationName} users with Salesmsg team members. One click is all it takes.`,
            }}
            className={styles.alert}
            actions={[
              {
                type: 'primary',
                variant: 'button',
                text: 'Map users',
                onClick: autoMapUsers,
                disabled: isLoading,
              },
            ]}
          />
        )}

        <TableSearch value={search} onChange={onSearchChange} />

        <div className={classnames(styles.table, { [styles.loading]: isLoading })}>
          <Table
            ariaLabel={'IntegrationSettingsUsers'}
            store={tableStore}
            withCheckbox
            columns={COLUMNS}
            rows={users}
            rowsCount={total}
            startPaginationSize={10}
            initPage={pagination.page}
            limit={pagination.limit}
            noPagination={!total}
            scrollToTOpTrigger={scrollToTOpTrigger}
            onChangePagination={onChangePagination}
            virtualization={{
              estimateRowHeight: 57,
            }}
            scrollbarProps={{
              autoHeight: true,
              autoHeightMin: 0,
              autoHeightMax: 610,
              viewClassName: styles.viewScrollClassName,
            }}
            headerLeftContentProps={{
              actionsContent: (
                <Button
                  text='Invite users'
                  icon='add'
                  typeBtn='contained'
                  contained='primary'
                  size='small'
                  onClick={onGroupInvite}
                />
              ),
            }}
            bottomTableContent={
              isShowBottomTableContent && (
                <tr>
                  <td colSpan={4}>
                    <div className={styles.emptySearch}>
                      <Typography
                        variant={'body-md-regular'}
                        ellipsis
                        ariaLabel={'empty_placeholder'}
                        color={'tertiary'}
                      >
                        No {integrationName} users
                      </Typography>
                    </div>
                  </td>
                </tr>
              )
            }
          />
        </div>
      </div>
    )
  }
)
